import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import type { EventStatus } from '@lib/enums/event-lifecycle.enum'
import { cn } from '@/lib/utils'

/**
 * A tag: a soft ground with its own ink, on Plan View's badge recipe — 22px tall,
 * the chip corner, 12px medium. `tone` picks the pair: `neutral` past, `success`
 * ok (booked, confirmed), `warning` hold (needs a look), `danger`, `info` blue
 * (an inquiry, something in progress, a role or a kind), `sage` for
 * brand-support chips. `info` has its own pair rather than the action's: the
 * action is forest, and a forest inquiry would read as booked. Pass `dot` for a
 * leading square mark. For an event's status, use {@link StatusBadge}, which
 * adds the mark shape that says the status without color.
 */
const badgeVariants = cva(
  'inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-sm px-[9px] text-xs font-medium leading-none',
  {
    variants: {
      tone: {
        neutral: 'bg-past-soft text-past-ink',
        success: 'bg-ok-soft text-ok-ink',
        warning: 'bg-hold-soft text-hold-ink',
        danger: 'bg-danger-soft text-danger-ink',
        info: 'bg-info-soft text-info-ink',
        sage: 'bg-sage-soft text-sage-ink',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  /** Render a leading status dot. */
  dot?: boolean
}

export function Badge({ className, tone, dot = false, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...props}>
      {dot ? <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-xs bg-current" /> : null}
      {children}
    </span>
  )
}

export { badgeVariants }

const STATUS: Record<EventStatus, { label: string; tone: BadgeTone; mark: string }> = {
  // Hollow: not yet a booking.
  enquiry: { label: 'Inquiry', tone: 'info', mark: 'h-[7px] w-[7px] rounded-xs border-[1.5px] border-current' },
  // Filled: booked and on the calendar.
  booked: { label: 'Booked', tone: 'success', mark: 'h-[7px] w-[7px] rounded-xs bg-current' },
  // Faded: it happened.
  past: { label: 'Past', tone: 'neutral', mark: 'h-[7px] w-[7px] rounded-xs bg-current opacity-55' },
  // A dash: struck off.
  cancelled: { label: 'Canceled', tone: 'danger', mark: 'h-0.5 w-2 rounded-xs bg-current' },
  // Hollow and faded: an inquiry that faded.
  lost: { label: 'Lost', tone: 'neutral', mark: 'h-[7px] w-[7px] rounded-xs border-[1.5px] border-current opacity-55' },
}

/**
 * An event's status: a word, a color and a mark shape, so it reads without the
 * color — a hollow square for an inquiry, filled for booked, faded for past, a
 * dash for canceled, a faded hollow square for a lost inquiry. One per row; anything needing attention is a separate
 * `warning` mark on the rows that need it.
 */
export function StatusBadge({ status, className }: { status: EventStatus; className?: string }) {
  const { label, tone, mark } = STATUS[status]
  return (
    <span className={cn(badgeVariants({ tone }), 'pl-2', className)}>
      <span aria-hidden className={cn('shrink-0', mark)} />
      {label}
    </span>
  )
}
